import React, { useEffect, useRef } from 'react';

/**
 * Erro de validação LOCAL, antes do envio.
 *
 * O contrato v2.1 não devolve `fields` no corpo de erro (só
 * `code`/`title`/`correlation_id` — ver `docs/decisions/2026-09-11-P0P1-CONFORMIDADE-DE-CONTRATO.md`),
 * diferente do `/api/v1` que `acesso/api.ts` ainda usa. Por isso aqui não há
 * erro de campo vindo do servidor: só a validação que a própria tela faz
 * antes de chamar a API, do mesmo jeito que `acesso/TelaDeAcesso.tsx` já
 * fazia para o e-mail e o código.
 */
export interface FieldError {
  readonly field: string;
  readonly code: string;
}

export interface ErroDescrito {
  readonly label: string;
  readonly message: string;
  /** Campo da tela para onde levar o foco. Ausente quando não há campo a focar. */
  readonly fieldId?: string;
}

export interface ResumoDeErrosProps {
  readonly errors: readonly FieldError[];
  readonly describe: (error: FieldError) => ErroDescrito;
}

/**
 * Resumo de erros no topo do formulário — mesmo padrão de `acesso/ResumoDeErros.tsx`.
 *
 * Cópia deliberada, não reaproveitamento: `acesso/` é o fluxo OTP que esta
 * pasta substitui e será removido, então `conta/` não depende dele.
 */
export const ResumoDeErros: React.FC<ResumoDeErrosProps> = ({ errors, describe }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errors.length > 0) {
      containerRef.current?.focus();
    }
  }, [errors]);

  if (errors.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      role="alert"
      tabIndex={-1}
      style={{
        backgroundColor: 'var(--nev-color-critical-soft)',
        borderLeft: '4px solid var(--nev-color-critical)',
        borderRadius: 'var(--nev-radius-control)',
        padding: 'var(--nev-space-4)',
        marginBottom: 'var(--nev-space-4)',
        fontFamily: 'var(--nev-font-body)',
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--nev-font-heading)',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: 'var(--nev-space-2)',
          color: 'var(--nev-text-primary)',
        }}
      >
        {errors.length === 1
          ? 'Há um item para revisar'
          : `Há ${String(errors.length)} itens para revisar`}
      </h2>

      <ul style={{ margin: 0, paddingLeft: 'var(--nev-space-5)' }}>
        {errors.map((error) => {
          const described = describe(error);
          const texto = `${described.label}: ${described.message}`;
          const alvo = described.fieldId;
          return (
            <li key={`${error.field}:${error.code}`} style={{ marginBottom: 'var(--nev-space-1)' }}>
              {alvo === undefined ? (
                texto
              ) : (
                <a
                  href={`#${alvo}`}
                  style={{ color: 'var(--nev-text-primary)' }}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(alvo)?.focus();
                  }}
                >
                  {texto}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
